import { LivePortraitFragment } from 'api/contentful/contentful.graphql.types'
import { ContentfulFAQ, getContentfulFAQ } from 'api/contentful/getContentfulFAQ'
import {
  ContentfulMediaMentions,
  getContentfulMediaMentions,
} from 'api/contentful/getContentfulMediaMention'
import { getContentfulLivePortraits } from 'api/contentful/memory'
import { ActionCards } from 'components/actions/ActionCards'
import { useLPActions } from 'components/actions/useLPActions'
import { Block } from 'components/Block'
import { HelpCarousel } from 'components/content/helpCarousel/HelpCarousel'
import { HomepageCallToAction } from 'components/content/homepage/homepageCallToAction/HomepageCallToAction'
import { LifeStoriesContextProvider } from 'components/content/lifeStories/LifeStoriesContext'
import { MediaMentionsRow } from 'components/content/mediaMentions/MediaMentionsRow'
import { MemoryHero } from 'components/content/memory/memoryHero/MemoryHero'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { Footer } from 'components/footer/Footer'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { Layout } from 'components/layout/Layout'
import { PrimaryLink } from 'components/Link'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { Spacer } from 'components/spacer/Spacer'
import { GetStaticProps } from 'next'
import { Trans, useTranslation } from 'next-i18next'
import React, { useEffect } from 'react'
import { ANALYTICS, mutateAnalyticsData } from 'utils/analytics'
import { colors } from 'utils/colors'
import { REVALIDATE_SECS } from 'utils/constants'
import { getIntlProps } from 'utils/i18n/getIntlProps'

export const iconProps = { size: '2rem', color: colors.gray[500] }

export const memoriesHomepageImages = [
  {
    src: '/images/forYou/image1.jpg',
    srcSet: `
              /images/forYou/image1.jpg 1x,
              /images/forYou/image1@2x.jpg 2x,
            `,
  },
  {
    src: '/images/forYou/image2.jpg',
    srcSet: `
              /images/forYou/image2.jpg 1x,
              /images/forYou/image2@2x.jpg 2x,
            `,
  },
  {
    src: '/images/forYou/image3.jpg',
    srcSet: `
              /images/forYou/image3.jpg 1x,
              /images/forYou/image3@2x.jpg 2x,
            `,
  },
  {
    src: '/images/forYou/image4.jpg',
    srcSet: `
              /images/forYou/image4.jpg 1x,
              /images/forYou/image4@2x.jpg 2x,
            `,
  },
  {
    src: '/images/forYou/image5.jpg',
    srcSet: `
              /images/forYou/image5.jpg 1x,
              /images/forYou/image5@2x.jpg 2x,
            `,
  },
]

export default function Page(props: {
  animatedPhotos: LivePortraitFragment[]
  mediaMentions: ContentfulMediaMentions
  faq: ContentfulFAQ
}) {
  const { t } = useTranslation('memory')
  useEffect(() => {
    mutateAnalyticsData({ page: 'memories_landing' })
    if (ANALYTICS.type !== 'animation_invited') mutateAnalyticsData({ type: 'animation' })
  }, [])

  const actions = useLPActions([
    'future_messages',
    'singing_portraits',
    'will',
    'digital_vault',
    'ahcd',
  ])

  return (
    <LifeStoriesContextProvider>
      <Layout
        header={<TopNavigationLandingPage variant="full" />}
        footer={<Footer variant="full" />}
      >
        <PageTitle title={t('memory.live.meta_title')} />
        <PageMeta
          description={t('memory.live.meta_desc')}
          image="/images/preview/live-portraits.png"
        />
        <MemoryHero
          previewAnimations={props.animatedPhotos}
          title={t('memory.live.title')}
          desc={t('memory.live.desc')}
        />
        <Spacer size={[2, 0.5]} />
        <Block>
          <MediaMentionsRow
            mediaMentions={props.mediaMentions}
            title={t('memory.live.media_mention_title')}
            hideQuotes
            alignCenterItems
            logoMaxHeightInRem={3}
            limit={5}
          />
        </Block>
        <Spacer size={[4, 6]} />
        <Block>
          <ActionCards
            variant="round-button"
            title={t('memory.live.action_cards_title')}
            actions={actions}
          />
        </Block>
        <Spacer size={[4, 6]} />
        <HelpCarousel />
        <Spacer size={[5, 6]} />
        <FaqTwoColumns content={props.faq} />
        <Spacer size={[5, 8]} />
        <HomepageCallToAction
          actionTitle={t('memory.live.action_section_title')}
          images={memoriesHomepageImages}
          description={
            <Trans
              t={t}
              i18nKey="memory.live.action_section_text"
              components={[<PrimaryLink key={0} href="/end-of-life-guide" />]}
            />
          }
        />
        <Spacer size={[2, 4]} />
      </Layout>
    </LifeStoriesContextProvider>
  )
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const animatedPhotos = await getContentfulLivePortraits()
  const mediaMentions = await getContentfulMediaMentions('global')
  const faq = await getContentfulFAQ('memories')

  return {
    props: {
      ...(await getIntlProps(ctx, ['life_stories', 'memory'])),
      animatedPhotos,
      mediaMentions,
      faq,
    },
    revalidate: REVALIDATE_SECS,
  }
}
